import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CardapioItemPedidoDTO, CardapioProdutoDTO } from '../../models/cardapio-digital/cardapio-digital.model';

/**
 * Servico UNIFICADO de gerenciamento do pedido para o cardapio digital.
 * Usa CardapioItemPedidoDTO (formato rico, com produto completo e opcoes detalhadas).
 * Persistencia via localStorage para manter o carrinho entre reloads.
 */
@Injectable({ providedIn: 'root' })
export class CardapioPedidoService {
  /** Prefixo das chaves de carrinho no localStorage (uma chave por shop). */
  private readonly CACHE_PREFIX = 'cardapio_pedido_';
  /** Slug do shop atual; cada shop possui seu proprio carrinho isolado. */
  private slugAtual: string | null = null;
  private readonly itens$ = new BehaviorSubject<CardapioItemPedidoDTO[]>([]);

  itensObservable = this.itens$.asObservable();

  get itens(): CardapioItemPedidoDTO[] { return this.itens$.value; }

  /**
   * Define o shop ativo (slug). O carrinho fica isolado por shop, pois shops
   * diferentes possuem itens proprios e nao devem compartilhar a sacola.
   * Ao trocar de shop, recarrega o carrinho daquele shop a partir do cache.
   */
  definirShop(slug: string | null): void {
    const novo = slug || 'preview';
    if (novo === this.slugAtual) return;
    this.slugAtual = novo;
    this.itens$.next(this.lerDoCache());
  }

  /** Chave de cache do carrinho do shop atual. */
  private chaveCache(): string {
    return `${this.CACHE_PREFIX}${this.slugAtual || 'preview'}`;
  }

  /**
   * Fingerprint para agrupar itens identicos (mesmo produto + mesmas opcoes).
   * Itens com as mesmas escolhas sao somados por quantidade.
   */
  private fingerprint(item: CardapioItemPedidoDTO): string {
    const opts = JSON.stringify(
      item.opcoesSelecionadas?.sort((a, b) => (a.grupoTitulo > b.grupoTitulo ? 1 : -1))
    );
    return `${item.produto.id}-${opts}`;
  }

  /** Adiciona item ou incrementa quantidade se identico */
  addItem(novo: CardapioItemPedidoDTO): void {
    const key = this.fingerprint(novo);
    const list = this.itens.slice();
    const idx = list.findIndex(i => this.fingerprint(i) === key);
    if (idx >= 0) {
      list[idx] = {
        ...list[idx],
        quantidade: list[idx].quantidade + novo.quantidade,
        valorTotal: list[idx].valorTotal + novo.valorTotal
      };
    } else {
      list.push(novo);
    }
    this.sincronizar(list);
  }

  /** Remove a linha inteira do item */
  removeItem(item: CardapioItemPedidoDTO): void {
    const key = this.fingerprint(item);
    const list = this.itens.slice();
    const idx = list.findIndex(i => this.fingerprint(i) === key);
    if (idx >= 0) {
      list.splice(idx, 1);
    }
    this.sincronizar(list);
  }

  /** Decrementa a quantidade em 1 */
  decrementItem(item: CardapioItemPedidoDTO): void {
    const key = this.fingerprint(item);
    const list = this.itens.slice();
    const idx = list.findIndex(i => this.fingerprint(i) === key);

    if (idx >= 0) {
      const itemToUpdate = list[idx];
      const unitValue = itemToUpdate.valorTotal / itemToUpdate.quantidade;

      if (itemToUpdate.quantidade > 1) {
        itemToUpdate.quantidade--;
        itemToUpdate.valorTotal -= unitValue;
      } else {
        list.splice(idx, 1);
      }
    }
    this.sincronizar(list);
  }

  /** Incrementa a quantidade em 1 */
  incrementItem(item: CardapioItemPedidoDTO): void {
    const key = this.fingerprint(item);
    const list = this.itens.slice();
    const idx = list.findIndex(i => this.fingerprint(i) === key);

    if (idx >= 0) {
      const itemToUpdate = list[idx];
      const unitValue = itemToUpdate.valorTotal / itemToUpdate.quantidade;

      itemToUpdate.quantidade++;
      itemToUpdate.valorTotal += unitValue;
    }
    this.sincronizar(list);
  }

  /** Limpa o carrinho */
  clear(): void { this.sincronizar([]); }

  /** Quantidade total de itens */
  getQuantidadeTotal(): number {
    return this.itens.reduce((acc, i) => acc + i.quantidade, 0);
  }

  /** Valor total do pedido */
  getTotalPedido(): number {
    return this.itens.reduce((acc, i) => acc + i.valorTotal, 0);
  }

  /** Quantidade de um produto especifico no carrinho */
  quantidadePorProduto(produtoId: number): number {
    return this.itens
      .filter((i) => i.produto.id === produtoId)
      .reduce((acc, i) => acc + i.quantidade, 0);
  }

  // --- Persistencia localStorage ---

  private sincronizar(itens: CardapioItemPedidoDTO[]): void {
    this.itens$.next(itens);
    try {
      localStorage.setItem(this.chaveCache(), JSON.stringify(itens));
    } catch {
      // localStorage indisponivel ou cheio
    }
  }

  private lerDoCache(): CardapioItemPedidoDTO[] {
    try {
      const raw = localStorage.getItem(this.chaveCache());
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed as CardapioItemPedidoDTO[];
    } catch {
      return [];
    }
  }
}
